import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { Overlay } from '~/ui/overlay/Overlay';
import { Modal } from '~/ui/modal/Modal';
import { CloseButton } from '~/ui/buttons/CloseButton';
import {
  AIScenarioForm,
  FormData,
  ChatBotTemplate
} from '~/training/sequence/templates/items/chatBot/components/forms/AIScenarioForm';
import { useFormSubmission } from '~/training/sequence/templates/items/chatBot/hooks/useFormSubmission';
import { validateChatBotForm } from '~/training/sequence/templates/items/chatBot/utils/validation';

const Wrapper = styled.div`
  max-height: 78vh;
  overflow-y: scroll;
  padding: 0 6px;

  .closeBtn {
    margin-left: calc(100% - 66px);
    margin-top: -6px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-family: ralewayBold;
  font-size: 18px;
  line-height: 1.5;
  color: #2e2e2e;
  margin: 6px 0px;
`;

export interface AIScenarioFormPopupProps {
  open: boolean;
  onClose: () => void;
  formData: FormData;
  onFormDataChange: (formData: FormData) => void;
  onSubmit: (formData: FormData) => void;
  templates?: ChatBotTemplate[];
  isSubmitting?: boolean;
  disabled?: boolean;
}

export const AIScenarioFormPopup: React.FC<AIScenarioFormPopupProps> = ({
  open,
  onClose,
  formData,
  onFormDataChange,
  onSubmit,
  templates,
  isSubmitting = false,
  disabled = false
}) => {
  const isValid = validateChatBotForm(formData);

  const { handleSubmit } = useFormSubmission({
    onSubmit: data => onSubmit(data),
    onClose,
    closeOnSubmit: true
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    if (open) {
      document.addEventListener('keydown', onKey);
    }
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return ReactDOM.createPortal(
    <Overlay open={open} opacity={0.5} onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        style={{ position: 'relative', zIndex: 9999 }}
      >
        <Modal
          open
          width="508px"
          horizontal="center"
          vertical="center"
          padding="40px 70px 34px"
          borderRadius="24px"
        >
          <Wrapper>
            <ModalHeader>
              <Title>Edit AI Scenario Practice</Title>
              <CloseButton
                onClick={onClose}
                type="close"
                ariaLabel="Close AI scenario modal"
              />
            </ModalHeader>

            <AIScenarioForm
              formData={formData}
              onFormDataChange={onFormDataChange}
              onSubmit={event => handleSubmit(event, formData)}
              isValid={isValid}
              templates={templates}
              isSubmitting={isSubmitting}
              isEditing={true}
              disabled={disabled}
            />
          </Wrapper>
        </Modal>
      </div>
    </Overlay>,
    document.body
  );
};
